import { createSelector } from 'reselect';
import { mapValues, pickBy } from 'lodash';
import { denormalize } from 'normalizr';
import { selectEntities } from './entities.js';
import { selectPortfolios } from './portfolio.js';

import { parentPortfolioReport as parentPortfolioReportSchema } from '../lib/schema.js';
import { selectCurrentOrganizationId } from './auth.js';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';

const SEARCH_PROJECTS_REQUEST =
  'changesync/projectReports/SEARCH_PROJECTS_REQUEST';
const SEARCH_PROJECTS_SUCCESS =
  'changesync/projectReports/SEARCH_PROJECTS_SUCCESS';
const SEARCH_PROJECTS_FAILURE =
  'changesync/projectReports/SEARCH_PROJECTS_FAILURE';
const RESET_SEARCH_RESULTS = 'changesync/projectReports/RESET_SEARCH_RESULTS';
const SEARCH_PROJECTS = 'changesync/projectReports/SEARCH_PROJECTS';

const initialState = {
  portfolioIds: [],
  projectIds: [],
  initiativeIds: {},
  query: {},
  pending: false,
  success: false,
  error: null,
};

export default (state = initialState, action) => {
  switch (action.type) {
    case SEARCH_PROJECTS_REQUEST:
      return {
        ...state,
        pending: true,
        success: false,
        error: null,
      };

    case SEARCH_PROJECTS_SUCCESS:
      const { entities } = action.payload;

      // Extract portfolios from parent portfolio structure
      const parentPortfolios =
        Object.keys(entities).length && entities.parentPortfolioReports
          ? Object.values(entities.parentPortfolioReports)
          : [];

      const portfoliosArray = parentPortfolios.flatMap(
        (pp) => pp.portfolios || []
      );

      const projects = portfoliosArray.reduce((allProjects, portfolio) => {
        return allProjects.concat(portfolio.projects || []);
      }, []);

      const initiativeIds = projects.map((project) => {
        return mapValues(
          pickBy(project, (_, key) => key.endsWith('Initiative')),
          (ids) => ids.map((id) => Number(id.replace(/[^\d]/g, '')))
        );
      });

      return {
        ...state,
        portfolioIds: action.payload.result,
        initiativeIds,
        query: action.meta.query,
        pending: false,
        success: true,
      };
    case SEARCH_PROJECTS_FAILURE:
      return {
        ...state,
        pending: false,
        error: action.error,
      };

    case RESET_SEARCH_RESULTS:
      return {
        ...state,
        projectReports: {
          ...initialState.projectReports,
        },
      };
    default:
      return state;
  }
};

export const searchProjects = (query) => (dispatch, getState) => {
  const { status, ...params } = query;
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        SEARCH_PROJECTS_REQUEST,
        SEARCH_PROJECTS_SUCCESS,
        SEARCH_PROJECTS_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/projects/search`,
      method: HTTP_METHODS.GET,
      query: {
        ...params,
        status: status === 'all' ? undefined : status,
      },
      schema: [parentPortfolioReportSchema],
    },
    meta: {
      query,
      debounce: {
        time: 500,
        key: SEARCH_PROJECTS,
      },
    },
  });
};

export const selectReportResultState = (state) => state.projectReports;

const selectSearchResultIds = createSelector(
  selectReportResultState,
  (state) => state.portfolioIds
);

const selectSearchInitiativeIds = createSelector(
  selectReportResultState,
  (state) => state.initiativeIds
);

export const selectReportResult = createSelector(
  selectSearchResultIds,
  selectSearchInitiativeIds,
  selectEntities,
  selectPortfolios,
  (resultIds, initiativeIds, entities, allPortfoliosData) => {
    // API always returns parent portfolio structure, denormalize it
    const parentPortfolios =
      denormalize(resultIds, [parentPortfolioReportSchema], entities) || [];

    // Create a map of portfolio ID to permissions for quick lookup
    const portfolioPermissionsMap = {};
    allPortfoliosData.forEach((portfolio) => {
      if (portfolio && portfolio.id) {
        portfolioPermissionsMap[portfolio.id] = {
          canRead: portfolio.permissions.can_read,
        };
      }
    });

    // Flatten all portfolios from all parent portfolios
    const allPortfolios = parentPortfolios.flatMap((pp) => pp.portfolios || []);

    const enhancedProjects = (projects) =>
      projects.map((project) => {
        const initiatives = initiativeIds[project.id] || {};
        const filteredInitiatives = mapValues(initiatives, (ids, type) => {
          return (project[type] || []).filter(({ id }) => ids.includes(id));
        });
        return {
          ...project,
          ...filteredInitiatives,
        };
      });

    const allProjects = allPortfolios.reduce((acc, portfolio) => {
      const projectsWithPortfolio = (portfolio.projects || []).map(
        (project) => {
          const permissions = portfolioPermissionsMap[portfolio.id];
          return {
            ...project,
            portfolioPermissions: permissions,
          };
        }
      );
      return acc.concat(projectsWithPortfolio);
    }, []);

    const finalProjects = enhancedProjects(allProjects);

    return {
      parentPortfolios: parentPortfolios.map((pp) => ({
        ...pp,
        portfolios: (pp.portfolios || []).map((portfolio) => ({
          ...portfolio,
          projects: enhancedProjects(portfolio.projects || []).map(
            (project) => ({
              ...project,
              portfolioPermissions: portfolioPermissionsMap[portfolio.id],
            })
          ),
        })),
      })),
      portfolios: allPortfolios.map((portfolio) => ({
        ...portfolio,
        projects: enhancedProjects(portfolio.projects || []).map((project) => ({
          ...project,
          portfolioPermissions: portfolioPermissionsMap[portfolio.id],
        })),
      })),
      projects: finalProjects,
    };
  }
);
